// Where the BKG search views put their filters: a rail beside the results
// from `lg` up, and a drawer behind a "Filters" button below it.

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Input,
  ModalClose,
  Stack,
  Typography,
} from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import SearchIcon from "@mui/icons-material/Search";
import { useWideLayout } from "../hooks/useWideLayout";
import { ActiveCountBadge } from "./ActiveCountBadge";
import { FilterUI, type FilterInterface } from "./SearchFilters";
import { activeFilterCount, type FilterValues } from "./filterState";

// The rail scrolls with the page rather than sticking with its own scroll:
// it grows as groups open, and a second scrollbar beside the page's fought it.
const RAIL_WIDTH_PX = 300;

// Pages with a rail are allowed wider than the default 1200px, so on large
// screens the rail's width comes out of the margins rather than the results.
export const RAIL_PAGE_SX = { maxWidth: 1600 };

type FilterRailLayoutProps = {
  schema: FilterInterface | undefined;
  schemaLoading: boolean;
  value: FilterValues;
  onChange: (next: FilterValues) => void;
  /** Changing it remounts the filter tree, e.g. when back/forward rewrites ?f=. */
  filterKey?: React.Key;
  /** Leaf paths shown up front; the rest go behind "More filters". */
  primaryFacets?: readonly string[];
  /** For the drawer's "Show N results" button. */
  resultCount: number;
  /** While a search runs the count is stale, so the button says so. */
  resultsLoading?: boolean;
  /** Tutorial anchor: the rail heading while the rail shows, else the Filters button. */
  tutorialRef?: React.RefCallback<HTMLElement>;
  children: React.ReactNode;
};

// Escape empties the box first, so in the drawer it only closes once empty.
const FilterSearchField: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Input
      size="sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Escape" && value) {
          event.stopPropagation();
          onChange("");
        }
      }}
      placeholder="Find a filter…"
      startDecorator={<SearchIcon />}
      endDecorator={
        value ? (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            aria-label="Clear filter search"
            onClick={() => {
              onChange("");
              inputRef.current?.focus();
            }}
          >
            <CloseIcon />
          </IconButton>
        ) : null
      }
      slotProps={{
        input: {
          ref: inputRef,
          role: "searchbox",
          "aria-label": "Find a filter",
        },
      }}
    />
  );
};

export const FilterRailLayout: React.FC<FilterRailLayoutProps> = ({
  schema,
  schemaLoading,
  value,
  onChange,
  filterKey,
  primaryFacets,
  resultCount,
  resultsLoading = false,
  tutorialRef,
  children,
}) => {
  const isWide = useWideLayout();
  // Collapsed on load, unless the page opened with filters set (a shared ?f=
  // link), so it's plain what the results are filtered by.
  const [railOpen, setRailOpen] = useState(() => Object.keys(value).length > 0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState("");
  const railId = useId();
  const titleId = useId();

  // A drawer left open when the viewport widens shouldn't pop back later.
  useEffect(() => {
    if (isWide) setDrawerOpen(false);
  }, [isWide]);

  const activeCount = useMemo(
    () => (schema ? activeFilterCount(schema, value) : 0),
    [schema, value],
  );

  // No schema means nothing to filter by, unless it is still on its way.
  const hasFilters = schema !== undefined || schemaLoading;
  const railShown = isWide && hasFilters && railOpen;

  const tree = schema ? (
    <FilterUI
      key={filterKey}
      filterData={schema}
      value={value}
      onChange={onChange}
      primaryFacets={primaryFacets}
      search={filterSearch}
    />
  ) : (
    <Typography level="body-sm" color="neutral">
      Loading filters…
    </Typography>
  );

  // Outside the drawer's scrolling area, so it stays in view there.
  const searchField = schema ? (
    <Box sx={{ px: 2, pt: 2 }}>
      <FilterSearchField value={filterSearch} onChange={setFilterSearch} />
    </Box>
  ) : null;

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
      {isWide && hasFilters && (
        // Hidden rather than unmounted, so reopening keeps the open groups.
        <Box
          component="aside"
          id={railId}
          aria-labelledby={titleId}
          hidden={!railOpen}
          sx={{ width: RAIL_WIDTH_PX, flexShrink: 0 }}
        >
          <Card sx={{ p: 0, gap: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ pl: 2, pr: 1, py: 1 }}
            >
              <Typography
                id={titleId}
                ref={railOpen ? tutorialRef : undefined}
                component="h2"
                level="title-md"
              >
                Filters
                <ActiveCountBadge count={activeCount} sx={{ ml: 1 }} />
              </Typography>
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                aria-label="Hide filters"
                aria-controls={railId}
                aria-expanded
                onClick={() => setRailOpen(false)}
              >
                <KeyboardDoubleArrowLeftIcon />
              </IconButton>
            </Stack>
            <Divider />
            {searchField}
            <Box sx={{ p: 2 }}>{tree}</Box>
          </Card>
        </Box>
      )}

      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        {hasFilters && !railShown && (
          <Button
            ref={tutorialRef}
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<FilterListIcon />}
            aria-controls={isWide ? railId : undefined}
            aria-expanded={isWide ? false : undefined}
            aria-haspopup={isWide ? undefined : "dialog"}
            onClick={() => (isWide ? setRailOpen(true) : setDrawerOpen(true))}
            sx={{ alignSelf: "flex-start" }}
          >
            Filters
            <ActiveCountBadge count={activeCount} sx={{ ml: 1 }} />
          </Button>
        )}
        {children}
      </Stack>

      {!isWide && hasFilters && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          size="md"
          slotProps={{
            content: {
              sx: {
                width: { xs: "100%", sm: 400 },
                display: "flex",
                flexDirection: "column",
              },
            },
          }}
        >
          <ModalClose />
          <DialogTitle>
            Filters
            <ActiveCountBadge count={activeCount} sx={{ ml: 1 }} />
          </DialogTitle>
          <Divider />
          {searchField}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
            {tree}
          </Box>
          <Divider />
          <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
            <Button
              variant="plain"
              color="neutral"
              disabled={activeCount === 0}
              onClick={() => onChange({})}
            >
              Clear all
            </Button>
            {/* Filtering is live; this only gets the drawer out of the way. */}
            <Button onClick={() => setDrawerOpen(false)} aria-live="polite">
              {resultsLoading
                ? "Updating results…"
                : `Show ${resultCount} result${resultCount === 1 ? "" : "s"}`}
            </Button>
          </Stack>
        </Drawer>
      )}
    </Box>
  );
};
